import { CallLink, ChatLink, ParamsType, CallLogStorage, ChatLogStorage } from 'rcx_adapter';

import { IncidentLog } from './IncidentLog';
import { InteractionLog } from './InteractionLog';
import { generatePatchData, generateCallData, generateChatData } from './logHelper';
import { LOGGING_TYPE } from '../../constants';
import type { RingCXCall, RingCXChat } from '../../interfaces';
import type { DataFetch } from '../../lib/DataFetch';
import type { ServiceNowAdapter } from '../ServiceNowAdapter';
import { UpdateStorageLogOptions, InitStorageLogOptions } from '../ServiceNowAdapter.interface';

interface LogRule {
    isCallLogDisabled: boolean;
    callLogSysId: string;
}

export class LogAdapter {
    private adapter: ServiceNowAdapter;
    private dataFetch: DataFetch;
    private _interactionLog: InteractionLog;
    private _incidentLog: IncidentLog;
    private _logRuleMap: Map<string, LogRule>;

    constructor(serviceNowAdapter: ServiceNowAdapter, dataFetch: DataFetch) {
        this.adapter = serviceNowAdapter;
        this.dataFetch = dataFetch;
        this._interactionLog = new InteractionLog(this.adapter, this.dataFetch);
        this._incidentLog = new IncidentLog(this.adapter, this.dataFetch);
        this._logRuleMap = new Map();
    }

    /**
     * If is digital type, return INTERACTION log type;
     * If is call type, return INTERACTION/INCIDENT log type based on admin config
     * @returns LOGGING_TYPE
     */
    private getLoggingType(paramsType: ParamsType) {
        if (paramsType === ParamsType.CALL) {
            return this.adapter.getCallLoggingType();
        }
        return LOGGING_TYPE.INTERACTION;
    }

    private getLogInstance(paramsType: ParamsType, loggingType?: LOGGING_TYPE) {
        const crmLoggingType = loggingType || this.getLoggingType(paramsType);
        if (crmLoggingType === LOGGING_TYPE.INCIDENT) {
            return this._incidentLog;
        }

        return this._interactionLog;
    }

    private async getSNLinksByCallIds(needSearchIds: string[], storageLogData: Record<string, any>) {
        const { incidentIds, interactionIds } = needSearchIds.reduce(
            (acc, id) => {
                const localData = storageLogData[id];
                const paramsType = localData?.crmChatLog?.channelType ? ParamsType.CHAT : ParamsType.CALL;
                const crmLoggingType = localData?.crmLoggingType || this.getLoggingType(paramsType);

                if (crmLoggingType === LOGGING_TYPE.INCIDENT) {
                    acc.incidentIds.push(id);
                } else {
                    acc.interactionIds.push(id);
                }
                return acc;
            },
            { incidentIds: [] as string[], interactionIds: [] as string[] }
        );

        const [incidentResult, interactionResult] = await Promise.all([
            incidentIds.length > 0 ? this._incidentLog.getLogByCallIds(incidentIds) : Promise.resolve({ result: [] }),
            interactionIds.length > 0
                ? this._interactionLog.getLogByCallIds(interactionIds)
                : Promise.resolve({ result: [] }),
        ]);

        return {
            incidentResult: incidentResult.result,
            interactionResult: interactionResult.result.filter((item: any) => item.u_call_id),
        };
    }

    private getSysIdByCallId(incidentResult: any, interactionResult: any, callId: string) {
        const interactionItem = interactionResult.find((item: any) => item.u_call_id === callId);
        if (interactionItem) {
            return interactionItem.sys_id;
        }

        const incident = incidentResult.find((item: any) => item.comments.includes(callId));
        return incident?.sys_id || '';
    }

    private async updateMissingLinks(
        needSearchIds: string[],
        storageLogData: Record<string, any>,
        logStorage: any,
        logKey: 'crmCallLog' | 'crmChatLog'
    ): Promise<void> {
        const { incidentResult, interactionResult } = await this.getSNLinksByCallIds(needSearchIds, storageLogData);

        const updates = needSearchIds
            .map((id) => {
                const sysId = this.getSysIdByCallId(incidentResult, interactionResult, id);
                if (!sysId) return null;

                const localData = storageLogData[id];
                if (localData) {
                    return logStorage.update(id, {
                        ...localData,
                        [logKey]: { ...localData[logKey], id: sysId },
                    });
                }

                return logStorage.save(id, {
                    uii: id,
                    [logKey]: { id: sysId },
                });
            })
            .filter(Boolean);

        await Promise.all(updates);
    }

    private buildLinks<T>(ids: string[], storageData: Record<string, any>, logKey: string): T[] {
        return ids.map((id) => {
            const localData = storageData[id];
            return localData?.[logKey]?.id ? localData : ({ uii: id } as T);
        });
    }

    private getCrmContact(rawData: any) {
        if (rawData.selectedName?.id) {
            return rawData.selectedName;
        }

        if (rawData.selectedRelatedTo?.id) {
            return rawData.selectedRelatedTo;
        }

        return {};
    }

    public async getStorageLog(callId: string, paramsType: ParamsType): Promise<any> {
        return paramsType === ParamsType.CALL ? CallLogStorage.getCallLog(callId) : ChatLogStorage.getChatLog(callId);
    }

    public viewLog(paramsType: ParamsType, sys_id: string, loggingType?: LOGGING_TYPE) {
        this.getLogInstance(paramsType, loggingType).viewLog(sys_id);
    }

    public async saveLogToSN({ callId, paramsType }: { callId: string; paramsType: ParamsType }) {
        const logRule = this._logRuleMap.get(callId);
        if (logRule?.isCallLogDisabled) {
            return;
        }
        const storageLogData = await this.getStorageLog(callId, paramsType);
        await this.getLogInstance(paramsType).saveLog({
            paramsType,
            storageLogData,
            callLogSysId: logRule?.callLogSysId,
        });
    }

    public async getCRMLink<T extends CallLink | ChatLink>(
        ids: string[],
        logStorage: any,
        logKey: 'crmCallLog' | 'crmChatLog'
    ): Promise<{ links: T[] }> {
        const storageLogData = await logStorage.getByIds(ids);
        const needSearchIds = ids.filter((id) => !storageLogData[id]?.[logKey]?.id);

        if (needSearchIds.length > 0) {
            await this.updateMissingLinks(needSearchIds, storageLogData, logStorage, logKey);
        }

        const updatedStorageLogData = await logStorage.getByIds(ids);

        return { links: this.buildLinks(ids, updatedStorageLogData, logKey) };
    }

    public async updateStorageLogAndContact({ id, rawData, paramsType }: UpdateStorageLogOptions) {
        const crmContact = this.getCrmContact(rawData);
        const savePromise =
            paramsType === ParamsType.CALL
                ? CallLogStorage.updateCallLogAndContact.bind(CallLogStorage)
                : ChatLogStorage.updateChatLogAndContact.bind(ChatLogStorage);
        const patchData = generatePatchData(rawData);
        const loggingType = this.getLoggingType(paramsType);
        const result = await savePromise(id, patchData, crmContact, loggingType);
        return result;
    }

    public async initStorageLog({ id, params, paramsType }: InitStorageLogOptions) {
        const savePromise =
            paramsType === ParamsType.CALL
                ? CallLogStorage.saveCallLog.bind(CallLogStorage)
                : ChatLogStorage.saveChatLog.bind(ChatLogStorage);

        const saveData =
            paramsType === ParamsType.CALL
                ? generateCallData(params as RingCXCall)
                : generateChatData(params as RingCXChat);
        const logData = await savePromise(id, saveData);
        return logData;
    }

    public async removeStorageLog(paramsType: ParamsType, id?: string) {
        if (id) {
            const removePromise =
                paramsType === ParamsType.CALL
                    ? CallLogStorage.remove.bind(CallLogStorage)
                    : ChatLogStorage.remove.bind(ChatLogStorage);
            return await removePromise(id);
        }
    }

    public setLogRule(id: string, rule: LogRule) {
        this._logRuleMap.set(id, rule);
    }

    public resetLogRule(id: string) {
        this._logRuleMap.delete(id);
    }
}
